'use client';

import { Box, TextField, Typography } from '@mui/material';
import { ApolloQueryResult, OperationVariables, useMutation } from '@parkchirp/api-client';
import React, { useCallback, useState } from 'react';

import { ADD_TENANT_TO_SUBSCRIPTION } from '../../mutations';
import { SetShowSnackbar, TenantListItem } from '../../types';
import { isValidEmail } from '../../utils';
import { EditorModal } from '../modals';

interface AddTenantModalProps {
	subscriptionId: string;
	handleClose: () => void;
	refetch: (variables?: Partial<OperationVariables>) => Promise<ApolloQueryResult<any>>;
	setShowSnackbar: SetShowSnackbar;
	tenantList?: TenantListItem[];
	tenantSubscriptionId?: string;
	theme: any;
}

function formatEmail(email: string) {
	return email.trim().toLocaleLowerCase();
}

export const AddTenantModal = ({
	subscriptionId,
	handleClose,
	refetch,
	tenantList,
	setShowSnackbar,
	tenantSubscriptionId,
	theme,
}: AddTenantModalProps) => {
	const [addTenant, { loading }] = useMutation(ADD_TENANT_TO_SUBSCRIPTION);
	const [newTenantEmail, setNewTenantEmail] = useState<string>('');
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

	const handleSubmit = useCallback(async () => {
		try {
			if (!newTenantEmail) {
				setShowSnackbar({
					message: 'Email is required',
					severity: 'error',
					autoHideDuration: 15000,
					onClose: () => setShowSnackbar(null),
				});
				return;
			}
			if (!isValidEmail(newTenantEmail.trim())) {
				setShowSnackbar({
					message: 'Please enter a valid email',
					severity: 'error',
					autoHideDuration: 15000,
					onClose: () => setShowSnackbar(null),
				});
				return;
			}
			const isAlreadyAdded = tenantList?.find(
				({ userEmail }) => formatEmail(userEmail) === formatEmail(newTenantEmail),
			);
			if (isAlreadyAdded) {
				setShowSnackbar({
					message: 'User has is already been added to this subscription',
					severity: 'error',
					autoHideDuration: 15000,
					onClose: () => setShowSnackbar(null),
				});
				return;
			}
			const response = await addTenant({
				variables: {
					email: formatEmail(newTenantEmail),
					subscriptionId,
					tenantSubscriptionId,
				},
				context: { apiVersion: 'v2' },
			});
			if (!response?.data?.addTenantToSubscription?.id) {
				throw new Error('Failed to add tenant user');
			}
			setShowSnackbar({
				message: 'Successfully added tenant user',
				severity: 'success',
				autoHideDuration: 5000,
				onClose: () => setShowSnackbar(null),
			});
			refetch();
			handleClose();
		} catch (error) {
			let message = 'Failed to add tenant user';
			if (error.message.includes('Missing user metadata')) {
				message = 'User account has not been verified yet. Please reach out to user.';
			}
			setShowSnackbar({
				message,
				severity: 'error',
				autoHideDuration: 15000,
				onClose: () => setShowSnackbar(null),
			});
		}
	}, [
		setShowSnackbar,
		refetch,
		newTenantEmail,
		tenantList,
		subscriptionId,
		addTenant,
		handleClose,
		tenantSubscriptionId,
	]);

	return (
		<EditorModal
			handleClose={() => handleClose()}
			handleSave={handleSubmit}
			hasUnsavedChanges={hasUnsavedChanges}
			headerText='Add Tenant User'
			containerClassName='addTenantModal'
			loading={loading}
			headerBorder
			theme={theme}
		>
			<Box style={{ margin: '10px 0' }}>
				<Typography>
					Pre-existing users will be able to sign into the portal with their current username and password.
				</Typography>
				<Typography style={{ margin: '10px 0 20px 0' }}>
					For new users, an account will be created and they will get an email with a temporary password.
				</Typography>
				<TextField
					id='tenant-email'
					label='Email'
					value={newTenantEmail}
					onChange={(e) => {
						setNewTenantEmail(e.target.value);
						setHasUnsavedChanges(true);
					}}
					style={{ width: '100%' }}
					variant='outlined'
					autoComplete='off'
					inputProps={{ 'data-cy': 'tenant-email-input' }}
				/>
			</Box>
		</EditorModal>
	);
};
